import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import { SearchBar, SearchResultsBreadcrumb, SearchResultsSidebar, useSearch } from 'features/search';
import { ButtonIcon, ConditionalLink, Flex, Hr, Loading, Page, Pagination, RichContent, Section, Select, Typography } from 'components';
import { GlobalSearchResponse, SearchResultTypeEnum } from 'features/search/types';
import { ProductTypeEnum } from 'features/products';
import { createUrl } from 'app/utils';
import { contentsTypes } from 'features/contents';
import { ConditionalLinkTypes } from 'components/conditionalLink';
import { useReports } from 'features/reports';
import { useResources } from 'features/resources';

const ResultItem = styled(Flex)`
  &:hover {
    background: ${({ theme }) => theme.colors.gray_100};
  }
`;

const ResultTitle = styled(Typography)`
  &:hover {
    text-decoration: underline;
  }
`;

const SearchResults = () => {
  const { results, showResults, fetchSearch, clearSearch, searchLoading, setSearched, clearSearchResults, currentPage, totalPages, limitPerPage } =
    useSearch();
  const {
    usingSearch,
    schools: { schoolB3 },
  } = useResources();

  const navigate = useNavigate();

  const { downloadReport, downloadLoading, downloadPercentage } = useReports();

  const [searchParams] = useSearchParams();
  const searchText = searchParams.get('q') || '';
  const searchPage = searchParams.get('page');
  const searchLimit = searchParams.get('limit');
  const searchProductsTypes = searchParams.get('types');
  const searchEnrolled = searchParams.get('enrolled');

  useEffect(() => {
    if (!usingSearch) navigate(createUrl('dashboard'));

    if (searchText)
      fetchSearch({
        searchText,
        page: searchPage ? +searchPage : undefined,
        limit: searchLimit ? +searchLimit : undefined,
        productTypes: searchProductsTypes ? searchProductsTypes.split(',').map((t) => +t) : [],
        enrolled: searchEnrolled ? Boolean(searchEnrolled) : null,
      });

    setSearched(true);

    return () => {
      clearSearch();
      clearSearchResults();
    };
  }, []);

  const createProductLink = (result: GlobalSearchResponse) =>
    createUrl(result.productClassUuid ? 'productClassDetails' : 'productDetails', [result.productSlug, result.productClassUuid]);

  const createTimelineLink = (result: GlobalSearchResponse) =>
    createUrl('productModule', [result.productSlug, result.productClassUuid, result.productClassTimelineUuid]);

  return (
    <Page title="Resultado da busca">
      {searchLoading && <Loading alpha />}
      {downloadLoading && <Loading alpha={0.8} label={`Por favor, aguarde.<br />${downloadPercentage ? `${downloadPercentage}%` : ``}`} />}

      <Flex flexDirection="column" bg="white">
        <SearchBar open />

        <Section flexDirection={{ m: 'column', d: 'row' }} flexWrap={{ d: 'nowrap' }}>
          <SearchResultsSidebar />

          <Flex flexDirection="column" py={{ m: 4, d: 5 }} flexGrow={1}>
            <Flex flexGrow={0} py={3} px={3}>
              <Typography type="type0" upper color="gray_400">
                {results?.length ? `${results?.length} resultados de busca encontrados` : `nenhum resultado de busca encontrado`}
              </Typography>
            </Flex>

            <Hr my={0} />

            {results?.length ? (
              <>
                {results?.map((result, index) => {
                  let link = '';
                  let linkType: ConditionalLinkTypes = 'internal';
                  let title: string | undefined = '';
                  let description: string | undefined = '';

                  let breadcrumb: {
                    label: string;
                    link?: string | undefined;
                  }[] = [];
                  let onclick = () => {};
                  let disable = false;

                  switch (result.searchResultType) {
                    default:
                    case SearchResultTypeEnum.Product:
                      title = `${result.productName}`;
                      description = result.productDescription;

                      if (result.productType === ProductTypeEnum.Course) {
                        link = createProductLink(result);
                        breadcrumb = [{ label: 'CURSO' }];
                      } else if (result.productType === ProductTypeEnum.Program) {
                        link = createUrl('programDetails', [result.productSlug]);
                        breadcrumb = [{ label: 'PROGRAMA' }];
                      } else if (result.productType === ProductTypeEnum.MasterClass) {
                        link = createUrl('masterClassDetails', [result.productSlug]);
                        breadcrumb = [{ label: 'MASTERCLASS' }];
                      } else if (result.productType === ProductTypeEnum.Journey) {
                        link = createUrl('journeys/details', [result.productSlug]);
                        breadcrumb = [{ label: 'JORNADA' }];
                      } else if (result.productType === ProductTypeEnum.Event) {
                        link = createUrl('events/details', [result.productSlug, result.productUuid, result.commentContentUuid]);
                        breadcrumb = [{ label: 'EVENTO' }];
                      } else if (result.productType === ProductTypeEnum.LiveMeeting) {
                        link = createUrl(result.productClassUuid ? 'liveDetails' : 'liveDetailsNoProduct', [
                          result.productSlug,
                          result.productClassUuid,
                          result.productUuid,
                        ]);
                        breadcrumb = [{ label: 'AULA AO VIVO' }];
                      } else if (result.productType === ProductTypeEnum.AnalysisReport) {
                        // title = `REL1 ${result.productName}`;

                        // onclick = () => downloadReport(result.analysisReportUuid!, result.analysisReportName!);
                        linkType = 'nolink';

                        // link = createUrl(result.productClassUuid ? 'productClassDetails' : 'productDetails', [result.productSlug, result.productClassUuid]);

                        breadcrumb = [{ label: 'RELATÓRIO/ANÁLISE' }];

                        disable = true;
                      } else if (result.productType === ProductTypeEnum.PracticeExam) {
                        link = createUrl('exams/start', [result.productUuid]);
                        breadcrumb = [{ label: 'AVALIAÇÃO1' }];
                      }

                      break;

                    case SearchResultTypeEnum.ProductClassTimeline:
                      title = `${result.productClassTimelineName}`;
                      link = createTimelineLink(result);

                      breadcrumb = [{ label: 'CURSO' }, { label: `${result.productName}`, link: createProductLink(result) }];
                      break;

                    case SearchResultTypeEnum.Content:
                      title = result.contentName;
                      description = result.contentDescription;

                      link =
                        result.productClassTimelineUuid && result.productClassUuid
                          ? result.productClassTimelineUuid
                            ? result.productClassUuid
                              ? createUrl('contentClassDetails', [
                                  result.productSlug,
                                  result.productClassUuid,
                                  result.productClassTimelineUuid,
                                  result.contentUuid,
                                  contentsTypes(result.contentType, result.externalDocumentType),
                                ])
                              : createUrl('contentDetails', [
                                  result.productSlug,
                                  result.productClassTimelineUuid,
                                  result.contentUuid,
                                  contentsTypes(result.contentType, result.externalDocumentType),
                                ])
                            : createUrl('productContentClassDetails', [
                                result.productSlug,
                                result.productClassUuid,
                                '',
                                result.contentUuid,
                                contentsTypes(result.contentType, result.externalDocumentType),
                              ])
                          : createUrl('productNoClassContent', [
                              result.productSlug,
                              result.productClassUuid,
                              result.productClassTimelineUuid,
                              result.contentUuid,
                              contentsTypes(result.contentType, result.externalDocumentType),
                            ]);

                      breadcrumb = [{ label: 'CURSO' }];

                      if (result.productName) {
                        breadcrumb.push({
                          label: result.productName,
                          link: createProductLink(result),
                        });
                      }

                      if (result.productClassTimelineName) {
                        breadcrumb.push({
                          label: result.productClassTimelineName,
                          link: createTimelineLink(result),
                        });
                      }

                      if (result.productClassDescription) {
                        breadcrumb.push({
                          label: result.productClassDescription,
                        });
                      }

                      break;

                    case SearchResultTypeEnum.PracticeExam:
                      title = result.practiceExamName;
                      description = result.practiceExamDescription;

                      link = createUrl('exams/start', [result.practiceExamUuid]);
                      breadcrumb = [{ label: 'AVALIAÇÃO' }];
                      break;

                    case SearchResultTypeEnum.AnalysisReport:
                      title = `${result.analysisReportName}`;

                      onclick = () => downloadReport(result.analysisReportUuid!, result.analysisReportDownloadName!);
                      linkType = 'nolink';

                      breadcrumb = [{ label: 'RELATÓRIO/ANÁLISE' }];
                      break;
                  }

                  return (
                    <Flex key={`search-${index}`} flexGrow={0} flexDirection="column" flexWrap="nowrap">
                      <ResultItem px={3} py={4} flexGrow={0} flexDirection="column" flexWrap="nowrap">
                        <Flex flexGrow={1} justifyContent="space-between" flexWrap={{ m: 'wrap', d: 'nowrap' }}>
                          <ConditionalLink
                            component={Flex}
                            type={linkType}
                            to={link}
                            onClick={onclick}
                            style={{ cursor: disable ? 'not-allowed' : 'pointer' }}
                          >
                            <ResultTitle type="type3" fontWeight="bold" color={disable ? 'black50' : schoolB3 ? 'dark_150' : 'primary_400'}>
                              {title}
                            </ResultTitle>
                          </ConditionalLink>

                          {/* <Typography type="type0" upper color="gray_400">
                        LABEL
                      </Typography> */}
                        </Flex>

                        {!!description && (
                          <RichContent defaultType="type0" textColor="black50" linkColor={schoolB3 ? 'dark_150' : undefined}>
                            {description}
                          </RichContent>
                        )}

                        <SearchResultsBreadcrumb items={breadcrumb} index={index} linkColor={schoolB3 ? 'dark_150' : undefined} />
                      </ResultItem>

                      <Hr my={0} />
                    </Flex>
                  );
                })}

                <Flex
                  flexGrow={0}
                  px={3}
                  py={4}
                  flexDirection={{ m: 'column', d: 'row' }}
                  alignItems={{ m: 'flex-start', d: 'center' }}
                  justifyContent={{ m: 'flex-start', d: 'space-between' }}
                >
                  <Flex alignItems="center" flexGrow={0} style={{ gap: 10 }}>
                    <Select
                      style={{ minWidth: 100 }}
                      value={limitPerPage}
                      onChange={(e) => {
                        fetchSearch({
                          searchText,
                          limit: +e.target.value,
                          productTypes: searchProductsTypes ? searchProductsTypes.split(',').map((t) => +t) : [],
                          enrolled: searchEnrolled ? Boolean(searchEnrolled) : null,
                        });

                        navigate(
                          `${createUrl('search')}?q=${searchText}&limit=${e.target.value}&page=1${
                            searchProductsTypes ? `&types=${searchProductsTypes}` : ''
                          }${searchEnrolled ? `&enrolled=${searchEnrolled}` : ''}`
                        );
                      }}
                      arrowColor={schoolB3 ? 'dark_150' : undefined}
                    >
                      {[10, 25, 50].map((limit) => (
                        <option key={`limit-${limit}`}>{limit}</option>
                      ))}
                    </Select>

                    <Typography type="type0" upper color="gray_400">
                      registros por página
                    </Typography>
                  </Flex>

                  <Flex flexGrow={0} mt={{ m: 3, d: 0 }}>
                    <Pagination
                      currentPage={currentPage || 1}
                      totalPages={totalPages || 0}
                      setPage={(page) => {
                        fetchSearch({
                          searchText,
                          page,
                          limit: limitPerPage,
                          productTypes: searchProductsTypes ? searchProductsTypes.split(',').map((t) => +t) : [],
                          enrolled: searchEnrolled ? Boolean(searchEnrolled) : null,
                        });

                        navigate(
                          `${createUrl('search')}?q=${searchText}${searchLimit ? `&limit=${searchLimit}&page=${page}` : ''}${
                            searchProductsTypes ? `&types=${searchProductsTypes}` : ''
                          }${searchEnrolled ? `&enrolled=${searchEnrolled}` : ''}`
                        );
                      }}
                      linkColor={schoolB3 ? 'dark_150' : undefined}
                    />
                  </Flex>
                </Flex>
              </>
            ) : showResults ? (
              <Flex flexDirection="column" alignItems="center" justifyContent="flex-start" pt={{ m: 4, d: 10 }}>
                <ButtonIcon icon="info" bg="transparent" color="black30" iconWidth={50} />

                <Typography mt={2} lineHeight={1.3} textAlign="center" color="black30">
                  Nenhum resultado de busca encontrado.
                  <br />
                  Por favor, tente novamente.
                </Typography>
              </Flex>
            ) : (
              <></>
            )}
          </Flex>
        </Section>
      </Flex>
    </Page>
  );
};

export default SearchResults;
